import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export const useReferral = () => {
  const { user } = useAuth();
  const [referralCode, setReferralCode] = useState<string>('');
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    totalEarnings: 0,
    activeReferrals: 0
  });
  const [referrals, setReferrals] = useState<any[]>([]);
  const [earnings, setEarnings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadReferralData();
    }
  }, [user]);

  const loadReferralData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Load referral code
      const { data: codeData } = await supabase
        .from('referral_codes')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (codeData) {
        setReferralCode(codeData.code);
        setReferralStats({
          totalReferrals: codeData.total_referrals || 0,
          totalEarnings: codeData.total_earnings || 0,
          activeReferrals: 0
        });
      }

      // Load referrals list
      const { data: referralsData } = await supabase
        .from('referrals')
        .select(`
          *,
          profiles:referee_id(display_name, avatar_url)
        `)
        .eq('referrer_id', user.id)
        .order('created_at', { ascending: false });

      if (referralsData) {
        setReferrals(referralsData);
        const active = referralsData.filter(
          r => r.status === 'active' && new Date(r.expires_at) > new Date()
        ).length;
        setReferralStats(prev => ({ ...prev, activeReferrals: active }));
      }

      // Load earnings
      const { data: earningsData } = await supabase
        .from('referral_earnings')
        .select(`
          *,
          profiles:referee_id(display_name, avatar_url)
        `)
        .eq('referrer_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (earningsData) {
        setEarnings(earningsData);
      }
    } catch (error) {
      console.error('Error loading referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReferralCode = async () => {
    if (!user) return;

    try {
      // Generate code
      const { data: newCode, error: codeError } = await supabase
        .rpc('generate_referral_code');

      if (codeError) throw codeError;

      // Create referral code record
      const { error: insertError } = await supabase
        .from('referral_codes')
        .insert({
          user_id: user.id,
          code: newCode
        });

      if (insertError) throw insertError;

      setReferralCode(newCode);
      toast.success('Referral code generated!');
    } catch (error) {
      console.error('Error generating referral code:', error);
      toast.error('Failed to generate referral code');
    }
  };

  const applyReferralCode = async (code: string) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const { data, error } = await supabase
        .rpc('apply_referral_code', {
          _referee_id: user.id,
          _referral_code: code.toUpperCase()
        });

      if (error) throw error;

      const result = data as { success: boolean; error?: string };

      if (result.success) {
        toast.success('Referral code applied! Your referrer will earn from your spending for 3 months.');
        return { success: true };
      } else {
        toast.error(result.error || 'Failed to apply referral code');
        return { success: false, error: result.error };
      }
    } catch (error: any) {
      console.error('Error applying referral code:', error);
      toast.error(error.message || 'Failed to apply referral code');
      return { success: false, error: error.message };
    }
  };

  return {
    referralCode,
    referralStats,
    referrals,
    earnings,
    loading,
    generateReferralCode,
    applyReferralCode,
    refresh: loadReferralData
  };
};
